import { initOpenApi } from "@/clients/open_api_injection";
import { FullScreenBackgroundContainer } from "@/components/container/full-screen-background-container";
import { DEFAULT_AUTHENTICATED_ROUTE } from "@/constants/app-routes";
import { LocalStorageKeys } from "@/constants/local-storage-keys";
import { setUserInfo } from "@/utils/sentry/tools";
import { setMixpanelUser } from "@/utils/mixpanel";
import { setSmartlookUser } from "@/utils/smartlook";
import { useUser } from "@clerk/clerk-react";
import { IonSpinner, useIonRouter } from "@ionic/react";
import { memo, useEffect, useRef } from "react";

interface PublicAuthGuardProps {
  children: React.ReactNode;
}

export const PublicAuthGuard = memo(function PublicAuthGuard({ children }: PublicAuthGuardProps) {
  const { isLoaded, user } = useUser();
  const router = useIonRouter();
  const hasRedirectedRef = useRef(false);
  const shouldRedirectAuthed = isLoaded && Boolean(user);
  const userInfoString = localStorage.getItem(LocalStorageKeys.USER_KEY);

  useEffect(() => {
    initOpenApi();
    if (!isLoaded) return;
    if (shouldRedirectAuthed) {
      if (hasRedirectedRef.current) return;
      hasRedirectedRef.current = true;
      if (router.routeInfo.pathname !== DEFAULT_AUTHENTICATED_ROUTE) {
        router.push(DEFAULT_AUTHENTICATED_ROUTE, "none", "replace");
      }
      if (!userInfoString) return;
      try {
        const userInfo = JSON.parse(userInfoString);
        setUserInfo(userInfo);
        void setSmartlookUser(userInfo.id, userInfo.email);
        setMixpanelUser(userInfo.id, userInfo.email);
      } catch (error) {
        console.error("Failed to parse user info", error);
      }
    }
  }, [isLoaded, router, shouldRedirectAuthed, userInfoString]);

  if (shouldRedirectAuthed) {
    return (
      <FullScreenBackgroundContainer
        imageClassName="bg-splash-screen-background"
        className="flex flex-col items-center justify-center"
        backdrop={false}
      >
        <IonSpinner name="crescent" className="h-12 w-12 text-white" />
      </FullScreenBackgroundContainer>
    );
  }

  return children;
});
